/**
 * Compact selected-rate pricing preview (no card chrome).
 * Full rate/escalator/grid controls stay in the Pricing side panel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { CaretDown, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import type { PreContractSummaryItem } from './PreContractModuleStep.types';
import {
    buildPreContractLivePpwQuotes,
    formatEscalatorPercent,
    formatPpaRate,
    pickRecommendedPartnerQuote,
    sortPartnerQuotesForDisplay,
    type PreContractPpwMetricsOverride,
    type PpwPartnerQuote,
} from '../lib/preContractPpwPricing';

export type PreContractSelectedPricingPreviewProps = {
    form: PreContractIntakeForm;
    onOpenDetails: () => void;
    onGenerateQuote?: () => void;
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    /** Prefer AI-selected design metrics when present (steps 3–4). */
    metricsOverride?: PreContractPpwMetricsOverride | null;
    /** Module-step metrics shown in the side rail (step 2). */
    summaryItems?: PreContractSummaryItem[];
    /** When true, render the richer card layout from the module-step mock. */
    moduleStepLayout?: boolean;
    /** Embedded inside a Solar product deal card (compact pricing snapshot). */
    productCardLayout?: boolean;
    /** Energy community sites should recommend Enfin without hiding other partners. */
    preferEnfinPartner?: boolean;
};

type RateSelection = 'all-in' | number;

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function formatMultiplier(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return value.toFixed(3);
}

function nearlyEqual(a: number, b: number, eps = 1e-6): boolean {
    return Math.abs(a - b) <= eps;
}

function PartnerPreview({
    quote,
    recommended,
}: {
    quote: PpwPartnerQuote;
    recommended?: boolean;
}) {
    return (
        <div
            className={`pci-pricing-preview__partner${
                quote.available ? '' : ' pci-pricing-preview__partner--muted'
            }${recommended ? ' pci-pricing-preview__partner--recommended' : ''}`}
        >
            <header className="pci-pricing-preview__partner-head">
                <strong>{quote.partner}</strong>
                {recommended ? <span className="pci-pricing-preview__partner-rec">Recommended</span> : null}
                {quote.interpolated ? <span>interpolated</span> : null}
            </header>
            {quote.available ? (
                <dl className="pci-pricing-preview__metrics">
                    <div>
                        <dt>PPW</dt>
                        <dd>{formatPpw(quote.ppw)}</dd>
                    </div>
                    <div>
                        <dt>EPC</dt>
                        <dd>{formatMoney(quote.epc, 0)}</dd>
                    </div>
                    <div>
                        <dt>Y1 monthly</dt>
                        <dd>{formatMoney(quote.estimatedMonthly, 0)}</dd>
                    </div>
                </dl>
            ) : (
                <p className="pci-pricing-preview__reason">
                    <WarningCircle size={12} weight="fill" aria-hidden />
                    {quote.reason || 'Unavailable'}
                </p>
            )}
        </div>
    );
}

export const PreContractSelectedPricingPreview: React.FC<
    PreContractSelectedPricingPreviewProps
> = ({
    form,
    onOpenDetails,
    onGenerateQuote,
    generateQuoteDisabled = false,
    generateQuoteDisabledReason,
    metricsOverride = null,
    summaryItems = [],
    moduleStepLayout = false,
    productCardLayout = false,
    preferEnfinPartner = false,
}) => {
    const [escalator, setEscalator] = useState(0.0299);
    const [rateSelection, setRateSelection] = useState<RateSelection>('all-in');

    const rateOverride = rateSelection === 'all-in' ? null : rateSelection;
    const result = useMemo(
        () => buildPreContractLivePpwQuotes(form, escalator, rateOverride, metricsOverride),
        [form, escalator, rateOverride, metricsOverride],
    );

    useEffect(() => {
        setRateSelection('all-in');
    }, [form.utilityRate, form.installState]);

    useEffect(() => {
        if (!result.availableEscalators.length) return;
        if (!result.availableEscalators.some((e) => nearlyEqual(e, escalator, 1e-4))) {
            const preferred = result.availableEscalators.find((e) => nearlyEqual(e, 0.0299, 1e-4));
            setEscalator(preferred ?? result.availableEscalators[result.availableEscalators.length - 1]);
        }
    }, [result.availableEscalators, escalator]);

    const rateSelectValue = rateSelection === 'all-in' ? 'all-in' : String(rateSelection);
    const bestQuote = useMemo(
        () => pickRecommendedPartnerQuote(result.quotes, { preferEnfin: preferEnfinPartner }),
        [result.quotes, preferEnfinPartner],
    );
    const displayQuotes = useMemo(
        () => sortPartnerQuotesForDisplay(result.quotes, { preferEnfin: preferEnfinPartner }),
        [result.quotes, preferEnfinPartner],
    );
    const metricByLabel = (label: string) =>
        summaryItems.find((item) => item.label === label)?.value ?? '—';
    const productionDisplay =
        result.annualProductionKwh > 0
            ? `${Math.round(result.annualProductionKwh).toLocaleString()} kWh`
            : metricByLabel('System Production');
    const usageDisplay = metricByLabel('Utility Usage');
    const systemSizeDisplay =
        result.systemKw > 0 ? `${result.systemKw.toFixed(2)} kW` : '—';

    const showRichLayout = moduleStepLayout || productCardLayout;
    const showActions = showRichLayout && onGenerateQuote;

    const content = (
        <>
            {showRichLayout ? (
                <>
                    <header className="pci-pricing-preview__hero">
                        <div className="pci-pricing-preview__hero-head">
                            <p className="pci-board-v2-label">Best pricing</p>
                            {bestQuote?.available ? (
                                <span className="pci-pricing-preview__badge">
                                    <CheckCircle size={12} weight="fill" aria-hidden />
                                    Recommended
                                </span>
                            ) : null}
                        </div>
                        {bestQuote ? (
                            <p className="pci-pricing-preview__partner-name">{bestQuote.partner}</p>
                        ) : null}
                        <p className="pci-pricing-preview__price">
                            {formatMoney(bestQuote?.epc, 0)}
                        </p>
                        {bestQuote?.available && bestQuote.interpolated ? (
                            <p className="pci-pricing-preview__interp">Interpolated rate</p>
                        ) : null}
                        {!bestQuote?.available && bestQuote?.reason ? (
                            <p className="pci-pricing-preview__reason pci-pricing-preview__reason--hero">
                                <WarningCircle size={12} weight="fill" aria-hidden />
                                {bestQuote.reason}
                            </p>
                        ) : null}
                    </header>

                    <section
                        className="pci-pricing-preview__stats"
                        aria-label="System and pricing metrics"
                    >
                        <dl className="pci-pricing-preview__stat-grid">
                            <div>
                                <dt>PPW</dt>
                                <dd>{formatPpw(bestQuote?.ppw)}</dd>
                            </div>
                            <div>
                                <dt>Multiplier</dt>
                                <dd>{formatMultiplier(result.multiplier)}</dd>
                            </div>
                            <div>
                                <dt>System size</dt>
                                <dd>{systemSizeDisplay}</dd>
                            </div>
                            <div>
                                <dt>Production</dt>
                                <dd>{productionDisplay}</dd>
                            </div>
                            <div className="pci-pricing-preview__stat-grid-span">
                                <dt>Est. annual usage</dt>
                                <dd>{usageDisplay}</dd>
                            </div>
                        </dl>
                    </section>

                    <section className="pci-pricing-preview__finance" aria-label="Financing">
                        <p className="pci-board-v2-label">Financing</p>
                        <dl className="pci-pricing-preview__finance-grid">
                            <div>
                                <dt>Rate (APR)</dt>
                                <dd>{formatEscalatorPercent(result.escalator)}</dd>
                            </div>
                            <div>
                                <dt>Term</dt>
                                <dd>180 mo</dd>
                            </div>
                        </dl>
                        <div className="pci-pricing-preview__monthly-block">
                            <span className="pci-pricing-preview__monthly-label">Monthly payment</span>
                            <strong className="pci-pricing-preview__monthly-value">
                                {formatMoney(bestQuote?.estimatedMonthly, 0)}
                            </strong>
                        </div>
                        {bestQuote?.available && !productCardLayout ? (
                            <p className="pci-pricing-preview__note" role="status">
                                {preferEnfinPartner
                                    ? 'Enfin is recommended for energy community properties. Other partners remain available.'
                                    : 'Lowest EPC among available partners at the current design multiplier.'}
                            </p>
                        ) : null}
                    </section>
                </>
            ) : (
                <p className="pci-board-v2-label">Selected pricing</p>
            )}

            {!showRichLayout ? (
                <>
                    <div className="pci-pricing-preview__rate">
                        <span>Rate</span>
                        <label className="pci-pricing-preview__rate-label">
                            <select
                                className="pci-pricing-preview__rate-select"
                                value={rateSelectValue}
                                aria-label="PPA rate"
                                onChange={(event) => {
                                    const value = event.target.value;
                                    if (value === 'all-in') {
                                        setRateSelection('all-in');
                                        return;
                                    }
                                    const next = Number(value);
                                    if (Number.isFinite(next) && next > 0) setRateSelection(next);
                                }}
                            >
                                <option value="all-in">
                                    {result.allInRate > 0
                                        ? `All-in ${formatPpaRate(result.allInRate)}`
                                        : 'All-in rate'}
                                </option>
                                {result.availableRates.map((rate) => (
                                    <option key={rate} value={String(rate)}>
                                        {formatPpaRate(rate)}
                                    </option>
                                ))}
                            </select>
                            <CaretDown size={12} weight="bold" aria-hidden />
                        </label>
                    </div>

                    {result.availableEscalators.length > 0 ? (
                        <div
                            className="pci-pricing-preview__escalators"
                            role="group"
                            aria-label="Escalator"
                        >
                            {result.availableEscalators.map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`pci-pricing-preview__esc${
                                        nearlyEqual(value, result.escalator, 1e-4)
                                            ? ' pci-pricing-preview__esc--on'
                                            : ''
                                    }`}
                                    aria-pressed={nearlyEqual(value, result.escalator, 1e-4)}
                                    onClick={() => setEscalator(value)}
                                >
                                    {formatEscalatorPercent(value)}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="pci-pricing-preview__reason">
                            <WarningCircle size={12} weight="fill" aria-hidden />
                            No tables for this state
                        </p>
                    )}

                    <div className="pci-pricing-preview__partners">
                        {displayQuotes.map((quote) => (
                            <PartnerPreview
                                key={quote.partner}
                                quote={quote}
                                recommended={
                                    preferEnfinPartner &&
                                    quote.partner === 'Enfin' &&
                                    quote.available
                                }
                            />
                        ))}
                    </div>
                </>
            ) : null}

            {showActions ? (
                <div className="pci-pricing-preview__actions">
                    <button
                        type="button"
                        className="pci-pricing-preview__generate"
                        disabled={generateQuoteDisabled}
                        title={generateQuoteDisabled ? generateQuoteDisabledReason : 'Open generate quote'}
                        onClick={onGenerateQuote}
                    >
                        Generate Quote
                    </button>
                    <button
                        type="button"
                        className="pci-pricing-preview__more"
                        onClick={onOpenDetails}
                    >
                        View full offer details →
                    </button>
                </div>
            ) : !showRichLayout ? (
                <button
                    type="button"
                    className="pci-pricing-preview__more"
                    onClick={onOpenDetails}
                >
                    More details
                </button>
            ) : (
                <button
                    type="button"
                    className="pci-pricing-preview__more"
                    onClick={onOpenDetails}
                >
                    View full offer details →
                </button>
            )}
        </>
    );

    if (productCardLayout) {
        return (
            <div className="pci-pricing-preview pci-pricing-preview--product-card">{content}</div>
        );
    }

    return (
        <aside
            className={`pci-pricing-preview${
                moduleStepLayout ? ' pci-pricing-preview--module-step' : ''
            }`}
            aria-label="Selected partner pricing"
        >
            {content}
        </aside>
    );
};

export default PreContractSelectedPricingPreview;
